/**
 * What a claimant needs told before they submit.
 *
 * Three facts decide whether a claim is approved quickly, refused, or paid
 * later than expected — and all three are invisible while you are filling the
 * form in:
 *
 *  - a receipt is required, and a claim without one cannot be approved
 *  - the category has a limit, and going over it needs a note
 *  - claims are paid with salary, so the date decides which month it lands in
 *
 * Saying them up front turns a rejection three days later into a correction
 * now. Each rule is derived from the category's own `limits`, which is the
 * server's source of truth for enforcement.
 */

/** The category as the picker knows it, with its rules attached. */
export const categoryRules = (category) => ({
  limit: Number(category?.limits?.claimLimit || 0),
  receiptRequired: Boolean(category?.limits?.receiptRequired),
  name: category?.name || null,
});

/**
 * Is this claim over its category's limit?
 *
 * A limit of 0 means "no limit", not "nothing allowed" — read the other way,
 * every claim in the company is over policy.
 */
export const isOverLimit = (amount, category) => {
  const { limit } = categoryRules(category);
  return limit > 0 && Number(amount) > limit;
};

/**
 * The live hint under the amount field.
 *
 * Phrased as what happens rather than as a refusal: going over the limit does
 * not block the claim, it changes who has to look at it.
 */
export const amountHint = (amount, category, money = (n) => `₹${n}`) => {
  const { limit, name } = categoryRules(category);
  if (!limit) return null;
  if (isOverLimit(amount, category)) {
    return {
      tone: "warning",
      text: `Over the ${money(limit)} limit for ${name || "this category"} — your manager will need a reason before approving.`,
    };
  }
  return { tone: "muted", text: `${name || "This category"} is limited to ${money(limit)} a claim.` };
};

/** The receipt rule, stated whether or not one has been attached yet. */
export const receiptHint = (hasReceipt, category) => {
  const { receiptRequired } = categoryRules(category);
  if (!receiptRequired) {
    return hasReceipt ? null : { tone: "muted", text: "A receipt is not required for this category, but it helps." };
  }
  return hasReceipt
    ? { tone: "success", text: "Receipt attached." }
    : { tone: "warning", text: "A claim without a receipt cannot be approved." };
};

/**
 * When the money actually arrives.
 *
 * Reimbursement rides on payroll, so a claim approved after the cut-off waits
 * for the following month. The employee's mental model is usually "a few days",
 * and the gap between that and six weeks is where the complaints come from.
 */
export const PAYROLL_CUTOFF_DAY = 25;

export const paymentTiming = (dateOfSpend, now = new Date(), cutoff = PAYROLL_CUTOFF_DAY) => {
  const ref = dateOfSpend ? new Date(dateOfSpend) : now;
  const day = Number.isNaN(ref.getTime()) ? now.getUTCDate() : ref.getUTCDate();
  const late = day > cutoff;
  return {
    late,
    text: late
      ? `Claims are paid with salary, not separately. Approved after the ${cutoff}th means it lands next month.`
      : "Claims are paid with salary, not separately, once approved.",
  };
};

/** Everything still stopping this claim from being submittable. */
export const blockers = ({ what, category, amount, date, receiptUrl } = {}) => {
  const out = [];
  if (!String(what || "").trim()) out.push("what it was for");
  if (!category) out.push("a category");
  const n = Number(amount);
  if (!amount || Number.isNaN(n) || n <= 0) out.push("an amount");
  if (!date) out.push("the date of spend");
  // Deliberately not a blocker: the server decides, and a claimant who cannot
  // find the receipt should still be able to submit and be asked for it.
  void receiptUrl;
  return out;
};

export const canSubmitClaim = (form) => blockers(form).length === 0;
